import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Page } from '@appwin/ui';
import { api, ApiError, type AdminAppDetail, type AdminRelease } from './api.js';
import { AddVersionForm } from './AddVersionForm.js';
import { EditAppForm } from './EditAppForm.js';
import { EditVersionForm } from './EditVersionForm.js';

/**
 * One app in the operator panel: every version, editable copy, and the actions
 * that actually matter (open on the site, add a version, delete).
 */
export function AppDetail({ appId, onBack }: { appId: string; onBack: () => void }) {
  const [data, setData] = useState<AdminAppDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingRelease, setEditingRelease] = useState<AdminRelease | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => {
    api
      .app(appId)
      .then(setData)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Ошибка'));
  }, [appId]);

  useEffect(load, [load]);

  if (error) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-danger">{error}</p>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <p className="py-16 text-center text-sm text-muted">Загрузка…</p>
      </Page>
    );
  }

  const { app, releases } = data;
  const published = releases.filter((release) => release.status === 'published');

  return (
    <Page>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border py-4">
        <div className="min-w-0">
          <button onClick={onBack} className="text-sm text-muted hover:text-fg">
            ← Приложения
          </button>
          <h1 className="mt-1 truncate text-xl font-semibold">{app.name}</h1>
          <p className="truncate text-xs text-muted">{app.id}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={`https://appwin-web.pages.dev/apps/${app.id}/`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 items-center rounded-lg border border-border px-3 text-sm text-muted hover:text-fg"
          >
            На сайте
          </a>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Править
          </Button>
          <Button size="sm" onClick={() => setAdding(true)}>
            Добавить версию
          </Button>
        </div>
      </header>

      <main className="space-y-6 py-6">
        <section>
          <h2 className="mb-2 text-sm font-semibold">Описание</h2>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <Row label="Категория" value={app.category} />
            <Row label="Источник" value={app.origin === 'winget' ? 'winget' : 'вручную'} />
            <Row label="Лицензия" value={app.license ?? '—'} />
            <Row label="Статус" value={app.status} />
          </dl>
          <p className="mt-3 max-w-prose text-sm text-muted">{app.description.en}</p>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              Версии <span className="text-muted">({published.length} опубликовано)</span>
            </h2>
          </div>

          {releases.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted">
              Версий нет. Добавьте ссылку на установщик — без неё страница приложения пустая.
            </p>
          ) : (
            <ul className="list-none space-y-1.5 p-0">
              {releases.map((release) => (
                <li
                  key={release.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {release.version}{' '}
                      <span className="font-normal text-muted">
                        {release.kind.toUpperCase()} · {release.scope} ·{' '}
                        {(release.sizeBytes / 1024 / 1024).toFixed(1)} МБ
                      </span>
                    </p>
                    {release.downloadUrl ? (
                      <a
                        href={release.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate text-xs text-accent hover:underline"
                      >
                        {release.downloadUrl}
                      </a>
                    ) : (
                      <p className="text-xs text-danger">нет ссылки</p>
                    )}
                    {release.silentArgs ? (
                      <p className="mt-0.5 font-mono text-xs text-muted">
                        {release.silentArgs.join(' ')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge tone={release.status === 'published' ? 'success' : 'warning'}>
                      {release.status === 'published' ? 'опубликована' : release.status}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => setEditingRelease(release)}>
                      Правка
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        if (!window.confirm(`Удалить версию ${release.version}?`)) return;
                        try {
                          await api.deleteRelease(release.id);
                          load();
                        } catch (cause) {
                          setError(cause instanceof ApiError ? cause.message : 'Ошибка');
                        }
                      }}
                    >
                      Удалить
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border-t border-border pt-4">
          {confirmDelete ? (
            <div className="rounded-lg border border-danger/40 bg-danger/10 p-4">
              <p className="text-sm font-medium">Удалить приложение целиком?</p>
              <p className="mt-1 text-xs text-muted">
                Исчезнут карточка и все {releases.length} версий. Отменить нельзя.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={async () => {
                    try {
                      await api.removeApp(app.id);
                      onBack();
                    } catch (cause) {
                      setError(cause instanceof ApiError ? cause.message : 'Ошибка');
                    }
                  }}
                >
                  Удалить навсегда
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                  Отмена
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              Удалить приложение
            </Button>
          )}
        </section>
      </main>

      {editing ? (
        <EditAppForm
          app={{
            id: app.id,
            name: app.name,
            category: app.category,
            origin: app.origin,
            status: app.status,
            unverified: app.unverified,
            latestVersion: published[0]?.version ?? null,
            releaseCount: published.length,
            updatedAt: app.updatedAt,
          }}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {adding ? (
        <AddVersionForm
          appId={app.id}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            load();
          }}
        />
      ) : null}

      {editingRelease ? (
        <EditVersionForm
          release={editingRelease}
          onClose={() => setEditingRelease(null)}
          onSaved={() => {
            setEditingRelease(null);
            load();
          }}
        />
      ) : null}
    </Page>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-muted">{label}</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}
